// src/components/chatWidget/AiActionsReviewDialog.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";

/**
 * Props:
 *  - groupId: string (required)
 *
 * Usage (for example in GroupAdmin page):
 *
 * <AiActionsReviewDialog groupId={group.id} />
 */
const AiActionsReviewDialog = ({ groupId }) => {
  const [open, setOpen] = useState(false);
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null); // which action is being approved/rejected
  const [error, setError] = useState(null);

  const fetchPendingActions = useCallback(async () => {
    if (!groupId) return;
    setLoading(true);
    setError(null);

    try {
      const res = await api.get(`/ai/groups/${groupId}/actions`, {
        params: { status: "pending" },
      });
      setActions(res.data || []);
    } catch (err) {
      console.error("Failed to load AI actions", err);
      setError("Unable to load AI suggestions right now.");
    } finally {
      setLoading(false);
    }
  }, [groupId]);

  // Load actions whenever dialog opens
  useEffect(() => {
    if (open) {
      fetchPendingActions();
    }
  }, [open, fetchPendingActions]);

  // ⬇️ Listen for "ai drafted actions" from the chat hook and auto-open
  useEffect(() => {
    const handler = (event) => {
      const detail = event.detail || {};
      // Only react if it's for THIS group
      if (!groupId || detail.groupId !== groupId) return;

      setOpen(true);
    };

    window.addEventListener("zii:aiActionsDrafted", handler);
    return () => {
      window.removeEventListener("zii:aiActionsDrafted", handler);
    };
  }, [groupId]);

  const handleApprove = async (action) => {
    if (!groupId || !action?.id) return;

    const errs = getValidationErrors(action);
    if (errs.length) {
      toast.error(`Cannot approve: ${errs.join(", ")}`);
      return;
    }

    setBusyId(action.id);
    try {
      await api.post(`/ai/groups/${groupId}/actions/${action.id}/approve`);
      toast.success("AI action approved & applied ✨");
      setActions((prev) => prev.filter((a) => a.id !== action.id));
    } catch (err) {
      console.error("Approve failed", err);
      const msg =
        err?.response?.data?.detail ||
        "Failed to apply this AI action. Check logs.";
      toast.error(msg);
    } finally {
      setBusyId(null);
    }
  };

  const handleReject = async (action) => {
    if (!groupId || !action?.id) return;
    setBusyId(action.id);
    try {
      await api.post(`/ai/groups/${groupId}/actions/${action.id}/reject`);
      toast.success("AI action rejected.");
      setActions((prev) => prev.filter((a) => a.id !== action.id));
    } catch (err) {
      console.error("Reject failed", err);
      const msg =
        err?.response?.data?.detail ||
        "Failed to reject this AI action. Check logs.";
      toast.error(msg);
    } finally {
      setBusyId(null);
    }
  };

  const renderTypeLabel = (type) => {
    switch (type) {
      case "send_email":
        return "Send email to members";
      case "create_event":
        return "Create event";
      case "create_feed":
        return "Create feed post";
      case "create_product":
        return "Create product";
      case "update_group_settings":
        return "Update group settings";
      case "create_poll_post":
        return "Create poll post";
      default:
        return type || "Unknown";
    }
  };

  const renderPayload = (payload) => {
    if (!payload || typeof payload !== "object") {
      return <pre className="text-xs text-slate-600">{String(payload)}</pre>;
    }
    return (
      <pre className="max-h-56 overflow-auto rounded-md bg-slate-950/5 p-2 text-[11px] text-slate-700">
        {JSON.stringify(payload, null, 2)}
      </pre>
    );
  };

  const fmtMoney = (v) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return "—";
    return n.toFixed(2);
  };

  const renderField = (label, value) => (
    <div className="flex justify-between gap-3">
      <span className="text-[11px] text-slate-500">{label}</span>
      <span className="text-[11px] font-medium text-slate-800 text-right break-words">
        {value ?? "—"}
      </span>
    </div>
  );

  const renderNiceDetails = (action) => {
    const p = action?.payload || {};

    switch (action?.type) {
      case "create_event":
        return (
          <div className="space-y-1">
            {renderField("Title", p.title)}
            {renderField("Date", p.event_date)}
            {renderField("Location", p.location)}
            {renderField(
              "Price",
              p.price != null ? fmtMoney(p.price) : "—"
            )}
            {renderField("Capacity", p.capacity ?? "—")}
            {p.description ? (
              <div className="pt-2 text-[11px] text-slate-700 whitespace-pre-wrap rounded-md bg-white border border-slate-200 p-2">
                {p.description}
              </div>
            ) : null}
          </div>
        );

      case "send_email":
        return (
          <div className="space-y-2">
            {renderField("Subject", p.subject)}
            <div className="text-[11px] text-slate-700 whitespace-pre-wrap rounded-md bg-white border border-slate-200 p-2">
              {p.message || "—"}
            </div>
          </div>
        );

      case "create_product":
        return (
          <div className="space-y-1">
            {renderField("Name", p.name)}
            {renderField("Price", p.price != null ? fmtMoney(p.price) : "—")}
            {renderField("Stock", p.stock ?? "—")}
            {renderField("Active", p.active === false ? "No" : "Yes")}
            {p.image ? renderField("Image", p.image) : null}
            {p.description ? (
              <div className="pt-2 text-[11px] text-slate-700 whitespace-pre-wrap rounded-md bg-white border border-slate-200 p-2">
                {p.description}
              </div>
            ) : null}
          </div>
        );

      case "create_feed": {
        const hasPoll = !!p.poll;
        const pollQ = p.poll?.question;
        const pollOpts = Array.isArray(p.poll?.options) ? p.poll.options : [];

        return (
          <div className="space-y-2">
            {p.title ? renderField("Title", p.title) : null}
            {p.external_url ? renderField("External URL", p.external_url) : null}
            {p.body ? (
              <div className="text-[11px] text-slate-700 whitespace-pre-wrap rounded-md bg-white border border-slate-200 p-2">
                {p.body}
              </div>
            ) : null}

            {hasPoll ? (
              <div className="rounded-md bg-white border border-slate-200 p-2 space-y-1">
                <div className="text-[11px] font-semibold text-slate-800">
                  Poll
                </div>
                {renderField("Question", pollQ)}
                {renderField(
                  "Multiple",
                  p.poll?.multiple ? "Yes" : "No"
                )}
                {pollOpts.length ? (
                  <ul className="mt-1 list-disc pl-4 text-[11px] text-slate-700">
                    {pollOpts.map((opt, idx) => (
                      <li key={`${opt}-${idx}`}>{String(opt)}</li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-[11px] text-slate-500">
                    No poll options provided.
                  </div>
                )}
              </div>
            ) : null}

            {!p.title && !p.body && !p.external_url && !hasPoll ? (
              <div className="text-[11px] text-slate-500">
                No details provided.
              </div>
            ) : null}
          </div>
        );
      }

      case "create_poll_post": {
        const pollQ = p.poll?.question;
        const pollOpts = Array.isArray(p.poll?.options) ? p.poll.options : [];

        return (
          <div className="space-y-2">
            {p.title ? renderField("Title", p.title) : null}
            {p.body ? (
              <div className="text-[11px] text-slate-700 whitespace-pre-wrap rounded-md bg-white border border-slate-200 p-2">
                {p.body}
              </div>
            ) : null}

            <div className="rounded-md bg-white border border-slate-200 p-2 space-y-1">
              <div className="text-[11px] font-semibold text-slate-800">
                Poll
              </div>
              {renderField("Question", pollQ)}
              {renderField("Multiple", p.poll?.multiple ? "Yes" : "No")}
              {pollOpts.length ? (
                <ul className="mt-1 list-disc pl-4 text-[11px] text-slate-700">
                  {pollOpts.map((opt, idx) => (
                    <li key={`${opt}-${idx}`}>{String(opt)}</li>
                  ))}
                </ul>
              ) : (
                <div className="text-[11px] text-slate-500">
                  No poll options provided.
                </div>
              )}
            </div>
          </div>
        );
      }

      case "update_group_settings": {
        const keys = p && typeof p === "object" ? Object.keys(p) : [];
        return (
          <div className="space-y-1">
            {keys.length ? (
              keys.map((k) => renderField(k, stringifySmall(p[k])))
            ) : (
              <div className="text-[11px] text-slate-500">
                No fields provided.
              </div>
            )}
          </div>
        );
      }

      default:
        return (
          <div className="text-[11px] text-slate-500">
            No renderer for this action type. See raw details below.
          </div>
        );
    }
  };

  const actionStats = useMemo(() => {
    const total = actions.length;
    const invalid = actions.reduce((n, a) => n + (getValidationErrors(a).length ? 1 : 0), 0);
    return { total, invalid };
  }, [actions]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>AI Suggestions for this group</DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            These drafts were proposed by the AI when you chatted as a group admin.
            Review the details, then approve to apply them or reject to discard.
          </DialogDescription>
        </DialogHeader>

        {/* small header stats */}
        <div className="mt-2 flex items-center justify-between">
          <div className="text-[11px] text-slate-600">
            Pending: <span className="font-semibold">{actionStats.total}</span>
            {actionStats.invalid ? (
              <>
                {" "}
                • Missing fields:{" "}
                <span className="font-semibold text-red-700">
                  {actionStats.invalid}
                </span>
              </>
            ) : null}
          </div>
          <Button variant="outline" size="xs" onClick={fetchPendingActions}>
            Refresh
          </Button>
        </div>

        <div className="mt-3 flex flex-col gap-3">
          {loading && (
            <div className="rounded-md bg-slate-50 px-3 py-2 text-xs text-slate-600">
              Loading AI suggestions…
            </div>
          )}

          {error && !loading && (
            <div className="rounded-md bg-red-50 px-3 py-2 text-xs text-red-700">
              {error}
            </div>
          )}

          {!loading && !error && actions.length === 0 && (
            <div className="rounded-md bg-slate-50 px-3 py-4 text-center text-xs text-slate-500">
              No pending AI suggestions for this group right now.
            </div>
          )}

          {!loading &&
            !error &&
            actions.map((action) => {
              const errs = getValidationErrors(action);
              const isInvalid = errs.length > 0;
              const isBusy = busyId === action.id;

              return (
                <div
                  key={action.id}
                  className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-3 text-xs"
                >
                  <div className="mb-2 flex items-start justify-between gap-2">
                    <div className="flex flex-col gap-1 min-w-0">
                      <span className="text-[11px] font-semibold text-slate-800">
                        {renderTypeLabel(action.type)}
                      </span>
                      {action.summary ? (
                        <span className="text-[11px] text-slate-600 break-words">
                          {action.summary}
                        </span>
                      ) : null}
                    </div>

                    <div className="flex items-center gap-2">
                      {isInvalid ? (
                        <Badge className="text-[10px] bg-red-600 text-white">
                          Needs info
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-[10px]">
                          Pending
                        </Badge>
                      )}
                    </div>
                  </div>

                  {/* validation errors (prevents approve 400 like "title and event_date are required") */}
                  {isInvalid ? (
                    <div className="mb-2 rounded-md bg-red-50 border border-red-200 px-2 py-2 text-[11px] text-red-700">
                      <div className="font-semibold">Missing required fields</div>
                      <ul className="list-disc pl-4">
                        {errs.map((e) => (
                          <li key={e}>{e}</li>
                        ))}
                      </ul>
                      <div className="mt-2 text-[11px] text-red-700/90">
                        Ask the AI to fill these fields, or edit the action payload before approving.
                      </div>
                    </div>
                  ) : null}

                  {/* Pretty details */}
                  <div className="rounded-md bg-white border border-slate-200 p-2">
                    {renderNiceDetails(action)}
                  </div>

                  {/* Raw details (optional) */}
                  <details className="mt-2">
                    <summary className="cursor-pointer text-[11px] font-medium text-slate-700">
                      View raw JSON
                    </summary>
                    <div className="mt-1">{renderPayload(action.payload)}</div>
                  </details>

                  {/* Footer: meta + actions */}
                  <div className="mt-2 flex items-center justify-between gap-2">
                    <span className="text-[10px] text-slate-500">
                      Requested:{" "}
                      {action.created_at
                        ? new Date(action.created_at).toLocaleString()
                        : "-"}
                    </span>

                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="xs"
                        onClick={() => handleReject(action)}
                        disabled={isBusy}
                      >
                        Reject
                      </Button>
                      <Button
                        size="xs"
                        onClick={() => handleApprove(action)}
                        disabled={isBusy || isInvalid}
                        title={
                          isInvalid
                            ? `Cannot approve: ${errs.join(", ")}`
                            : undefined
                        }
                      >
                        {isBusy ? "Applying..." : "Approve & apply"}
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
        </div>
      </DialogContent>
    </Dialog>
  );
};

function stringifySmall(v) {
  if (v == null) return "—";
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}

/**
 * Frontend validation to prevent approve calls that will 400 in backend.
 * Mirrors backend requirements in aiActionHandlers.js.
 */
function getValidationErrors(action) {
  const type = action?.type;
  const p = action?.payload || {};
  const errors = [];

  if (!type) return ["Missing: type"];

  if (type === "send_email") {
    if (!p.subject) errors.push("Missing: subject");
    if (!p.message) errors.push("Missing: message");
  }

  if (type === "create_event") {
    if (!p.title) errors.push("Missing: title");
    if (!p.event_date) errors.push("Missing: event_date");
  }

  if (type === "create_product") {
    if (!p.name) errors.push("Missing: name");
  }

  if (type === "update_group_settings") {
    const allowed = [
      "name",
      "description",
      "logo",
      "visibility",
      "require_subscription",
      "membership_price",
      "membership_period",
      "currency",
      "max_capacity",
    ];
    const hasAny = allowed.some((k) => k in p);
    if (!hasAny) errors.push("Missing: at least one updatable field");
  }

  if (type === "create_feed") {
    const hasSomething =
      !!p.title || !!p.body || !!p.external_url || !!p.poll;
    if (!hasSomething) {
      errors.push("Missing: title/body/external_url/poll");
    }
    // note: backend allows poll to be present but empty; we won't block it here
  }

  if (type === "create_poll_post") {
    if (!p.poll) errors.push("Missing: poll");
    if (p.poll && !p.poll.question) errors.push("Missing: poll.question");
    const opts = Array.isArray(p.poll?.options) ? p.poll.options : [];
    if (p.poll && opts.length < 2) errors.push("Missing: poll.options (>= 2)");
  }

  return errors;
}

export default AiActionsReviewDialog;
